import {
	Conversation,
	ConversationContent,
	ConversationEmptyState,
	ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputSubmit,
	PromptInputTextarea,
	type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import {
	Suggestion,
	Suggestions,
} from "@/components/ai-elements/suggestion";
import {
	Task,
	TaskContent,
	TaskItem,
	TaskTrigger,
} from "@/components/ai-elements/task";
import { AgentAvatar } from "./AgentAvatar.js";
import { AgentChrome } from "./AgentChrome.js";
import { cn } from "./cn.js";
import { Message } from "./Message.js";
import type { ChatPersona } from "./mairko-chat.js";
import type {
	CardAction,
	ChatCompanyResearch,
	ChatMessage,
	ChatSiteDesign,
} from "./messages.js";

export type { ChatPersona };

type Progress = {
	current: number;
	total: number;
	label: string;
};

type Props = {
	agent: ChatPersona;
	messages: ChatMessage[];
	draft: string;
	onDraftChange: (value: string) => void;
	onSubmit: (text: string) => void;
	suggestions: string[];
	canType: boolean;
	typing: boolean;
	progress?: Progress | null;
	readyLabel?: string;
	onOpenInfo?: () => void;
	onBack?: () => void;
	onCardAction?: (action: CardAction) => void;
	onCompanyResearchPatch?: (
		packId: string,
		patch: Partial<ChatCompanyResearch>,
	) => void;
	onSiteDesignPatch?: (siteId: string, patch: Partial<ChatSiteDesign>) => void;
	onConfirmCompanyPack?: (packId: string) => void;
	confirmingPackId?: string | null;
	composerHint?: string;
	placeholder?: string;
	/** Optional live task trail under the chrome (AI Elements Task). */
	taskTitle?: string | null;
	taskItems?: string[];
};

/** Main product chat surface — AI Elements conversation + composer. */
export function AgentChat({
	agent,
	messages,
	draft,
	onDraftChange,
	onSubmit,
	suggestions,
	canType,
	typing,
	progress = null,
	readyLabel,
	onOpenInfo,
	onBack,
	onCardAction,
	onCompanyResearchPatch,
	onSiteDesignPatch,
	onConfirmCompanyPack,
	confirmingPackId,
	composerHint,
	placeholder = "Message…",
	taskTitle = null,
	taskItems = [],
}: Props) {
	const empty = messages.length === 0 && !typing;

	return (
		<div className="chat">
			<AgentChrome
				agent={agent}
				progress={progress}
				readyLabel={readyLabel}
				onOpenInfo={onOpenInfo}
				onBack={onBack}
			/>

			{taskTitle && taskItems.length > 0 ? (
				<div className="agent-chat-task">
					<Task defaultOpen={typing}>
						<TaskTrigger title={taskTitle} />
						<TaskContent>
							{taskItems.map((item) => (
								<TaskItem key={item}>{item}</TaskItem>
							))}
						</TaskContent>
					</Task>
				</div>
			) : null}

			<Conversation className="min-h-0">
				<ConversationContent className="gap-5 px-[22px] py-4">
					{empty ? (
						<ConversationEmptyState
							className="min-h-[40vh]"
							title={`Chat with ${agent.name}`}
							description={agent.lede}
							icon={<AgentAvatar agent={agent} size="lg" />}
						/>
					) : (
						messages.map((message) => (
							<Message
								key={message.id}
								message={message}
								onCardAction={onCardAction}
								onCompanyResearchPatch={onCompanyResearchPatch}
								onSiteDesignPatch={onSiteDesignPatch}
								onConfirmCompanyPack={onConfirmCompanyPack}
								confirmingPackId={confirmingPackId}
							/>
						))
					)}
				</ConversationContent>
				<ConversationScrollButton />
			</Conversation>

			<div className="composer">
				{canType && suggestions.length > 0 && (
					<Suggestions className="mb-2 px-0.5">
						{suggestions.map((suggestion) => (
							<Suggestion
								key={suggestion}
								suggestion={suggestion}
								onClick={(value) => onDraftChange(value)}
							/>
						))}
					</Suggestions>
				)}
				<PromptInput
					className={cn(!canType && "opacity-70")}
					onSubmit={(message: PromptInputMessage) => {
						onSubmit(message.text);
					}}
				>
					<PromptInputBody>
						<PromptInputTextarea
							value={draft}
							onChange={(event) => onDraftChange(event.target.value)}
							placeholder={canType ? placeholder : "Follow the prompts…"}
							disabled={!canType}
						/>
					</PromptInputBody>
					<PromptInputFooter>
						<span className="composer-hint px-1">
							{composerHint ??
								(canType
									? "Return to send · Shift-Return for newline"
									: "Follow the prompts in the chat")}
						</span>
						<PromptInputSubmit
							disabled={!canType || !draft.trim()}
							status={typing ? "submitted" : "ready"}
						/>
					</PromptInputFooter>
				</PromptInput>
			</div>
		</div>
	);
}
